import { useEffect, useState, type ReactNode } from "react";
import { useProfiles } from "../profiles/ProfileContext";
import { PageHeader, StatTile, Panel } from "../components/Surface";
import { ListTable, ListRow } from "../components/ListTable";
import { BarBreakdown, TrendArea, statusTone } from "../components/Charts";
import { labelize } from "../lib/format";
import { countByStatus, bucketByWeek, sumBy, winRate, kpis } from "../lib/analytics";
import { listJobs, myJobs, myApplications, HIRING_TYPES, type MyJob, type JobApplication } from "../lib/jobs";
import { listLoads, myLoads, myBids, laneLabel, LOAD_POST_TYPES, LOAD_BID_TYPES, type MyLoad, type LoadBid } from "../lib/loads";
import { getAnalyticsSummary, type AnalyticsSummary } from "../lib/analytics-api";

interface Row {
  id: string;
  title: string;
  meta: string;
  status: string;
  created_at: string;
  to: string;
}

export function Analytics() {
  const { active } = useProfiles();
  const type = active?.type;
  const isHiring = type ? HIRING_TYPES.includes(type) : false;
  const isPoster = type ? LOAD_POST_TYPES.includes(type) : false;
  const isBidder = type ? LOAD_BID_TYPES.includes(type) : false;
  const isDriver = type === "driver";

  const [market, setMarket] = useState({ openJobs: 0, openLoads: 0 });
  const [postings, setPostings] = useState<MyJob[]>([]);
  const [loads, setLoads] = useState<MyLoad[]>([]);
  const [apps, setApps] = useState<JobApplication[]>([]);
  const [bids, setBids] = useState<LoadBid[]>([]);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Each fetch is independent so one failure can't blank the rest of the dashboard.
    async function safe<T>(fn: () => Promise<T>): Promise<T | null> {
      try {
        return await fn();
      } catch {
        return null;
      }
    }
    async function run() {
      if (!active) {
        setLoading(false);
        return;
      }
      const [j, l, s] = await Promise.all([safe(listJobs), safe(listLoads), safe(() => getAnalyticsSummary(active.id))]);
      if (!cancelled) setMarket({ openJobs: j?.jobs.length ?? 0, openLoads: l?.loads.length ?? 0 });
      if (!cancelled) setSummary(s);
      if (isHiring) {
        const r = await safe(() => myJobs(active.id));
        if (r && !cancelled) setPostings(r.jobs);
      }
      if (isPoster) {
        const r = await safe(() => myLoads(active.id));
        if (r && !cancelled) setLoads(r.loads);
      }
      if (isDriver) {
        const r = await safe(() => myApplications(active.id));
        if (r && !cancelled) setApps(r.applications);
      }
      if (isBidder) {
        const r = await safe(() => myBids(active.id));
        if (r && !cancelled) setBids(r.bids);
      }
      if (!cancelled) setLoading(false);
    }
    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  // Rows for the primary activity dataset (role-prioritized).
  const postingRows: Row[] = postings.map((p) => ({ id: p.id, title: p.title, meta: `${p.application_count} applicant${p.application_count === 1 ? "" : "s"}`, status: p.status, created_at: p.created_at, to: `/jobs/${p.id}` }));
  const loadRows: Row[] = loads.map((l) => ({ id: l.id, title: laneLabel(l.origin, l.destination), meta: `${l.bid_count} bid${l.bid_count === 1 ? "" : "s"}`, status: l.status, created_at: l.created_at, to: `/loads/${l.id}` }));
  const appRows: Row[] = apps.map((a) => ({ id: a.id, title: a.title ?? "—", meta: a.poster_name ?? "", status: a.status, created_at: a.created_at, to: `/jobs/${a.job_id}` }));
  const bidRows: Row[] = bids.map((b) => ({ id: b.id, title: laneLabel(b.origin ?? "—", b.destination ?? "—"), meta: `$${b.amount.toLocaleString()}`, status: b.status, created_at: b.created_at, to: `/loads/${b.load_id}` }));

  const primary = isDriver ? { label: "Applications", rows: appRows } : isHiring ? { label: "Postings", rows: postingRows } : isPoster ? { label: "Loads", rows: loadRows } : { label: "Bids", rows: bidRows };

  const breakdown = countByStatus(primary.rows).map((s) => ({ label: labelize(s.status), value: s.count, tone: statusTone(s.status) }));
  const trend = bucketByWeek(primary.rows, 8);
  const recent = [...primary.rows].sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at)).slice(0, 8);

  const accepted = appRows.filter((a) => a.status === "accepted").length;
  const awarded = bidRows.filter((b) => b.status === "awarded").length;

  const tiles: ({ icon: string; label: string; value: ReactNode; tone: "accent" | "verified" | "info" | "danger"; to: string })[] = [
    { icon: "jobs", label: "Open jobs", value: market.openJobs, tone: "accent", to: "/jobs" },
    { icon: "loads", label: "Open loads", value: market.openLoads, tone: "info", to: "/loads" },
    ...(isHiring ? [
      { icon: "folder", label: "My postings", value: postings.length, tone: "verified" as const, to: "/jobs/mine" },
      { icon: "applications", label: "Applications received", value: sumBy(postings, (p) => p.application_count), tone: "accent" as const, to: "/jobs/mine" },
    ] : []),
    ...(isPoster ? [
      { icon: "folder", label: "My loads", value: loads.length, tone: "verified" as const, to: "/loads/mine" },
      { icon: "bids", label: "Bids received", value: sumBy(loads, (l) => l.bid_count), tone: "accent" as const, to: "/loads/mine" },
    ] : []),
    ...(isDriver ? [
      { icon: "applications", label: "My applications", value: apps.length, tone: "verified" as const, to: "/applications" },
      { icon: "verify", label: "Win rate", value: `${winRate(apps.length, accepted)}%`, tone: "verified" as const, to: "/applications" },
    ] : []),
    ...(isBidder && !isPoster ? [
      { icon: "bids", label: "My bids", value: bids.length, tone: "accent" as const, to: "/bids" },
      { icon: "verify", label: "Awarded", value: awarded, tone: "verified" as const, to: "/bids" },
    ] : []),
  ];

  if (!active) return <p className="p-10 font-mono text-sm text-muted">Create a profile to see your analytics.</p>;

  return (
    <div className="grid gap-6">
      <PageHeader title="Analytics" subtitle={`Performance for ${active.display_name ?? labelize(active.type)}.`} />

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : (
        <>
          {summary && (
            <Panel title="Lifetime KPIs">
              <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="kpi-band">
                {kpis(summary).map((k) => (
                  <div key={k.label}>
                    <dt className="font-mono text-[11px] uppercase tracking-widest text-muted">{k.label}</dt>
                    <dd className="font-display text-2xl font-black tracking-tight">{k.value}</dd>
                  </div>
                ))}
              </dl>
            </Panel>
          )}

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {tiles.map((t) => <StatTile key={t.label} {...t} />)}
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="grid content-start gap-6 lg:col-span-2">
              <Panel title={`${primary.label} · last 8 weeks`}>
                <TrendArea points={trend} />
              </Panel>

              <Panel title={`Recent ${primary.label.toLowerCase()}`}>
                {recent.length === 0 ? (
                  <p className="font-mono text-sm text-muted">No activity yet.</p>
                ) : (
                  <ListTable columns={[primary.label, "Status", "When"]}>
                    {recent.map((r) => (
                      <ListRow key={r.id} to={r.to} title={r.title} meta={r.meta} status={r.status} right={new Date(r.created_at).toLocaleDateString()} />
                    ))}
                  </ListTable>
                )}
              </Panel>
            </div>

            <div className="grid content-start gap-6">
              <Panel title={`${primary.label} by status`}>
                <BarBreakdown items={breakdown} />
              </Panel>

              <Panel title="Market snapshot">
                <div className="grid gap-2 font-mono text-sm">
                  <div className="flex justify-between"><span className="text-muted">Open jobs</span><span className="font-bold">{market.openJobs}</span></div>
                  <div className="flex justify-between"><span className="text-muted">Open loads</span><span className="font-bold">{market.openLoads}</span></div>
                </div>
              </Panel>

              <Panel title="Grow">
                <p className="text-sm text-muted">
                  {isHiring || isPoster
                    ? "Complete, verified postings with clear pay and requirements attract more qualified operators."
                    : "A complete, verified profile and a quick, specific note get more responses."}
                </p>
              </Panel>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
